import { forwardRef, useRef, useState } from "react";
import { Badge, Button, Card, Col, FormGroup, FormLabel, Modal, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import LoadingIcon from "../../../Icon/LoadingIcon";
import { formatZipCode, formatPhoneNumber, formatCNPJ } from "../../../../../utils/FormatUtils";
import { formatDateTime } from "../../../../../utils/DateUtils";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import InputText from "../../../Forms/Element/InputText";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { successAlert } from "../../../alert/defaultAlert";
import { ApiError } from "../../../Common/ApiError";
import "./NonTaxCouponGenerator.css";

const NonTaxCouponGenerator = forwardRef((props, ref) => {

    const { t } = useTranslation();
    const { order } = props;
    const componentRef = useRef();
    const company = order.tenant;
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [email, setEmail] = useState(order.customer?.email || '');
    const [sendingEmail, setSendingEmail] = useState(false);

    const handleSendEmail = async () => {
        if (!email) {
            return;
        }

        setSendingEmail(true);
        try {
            await axiosInstance.post(`${Api.sales.order}/${order.id}/send-coupon-email`, { email });
            successAlert(t('coupon_sent_successfully'));
            setShowEmailModal(false);
        } catch (error) {
            ApiError(error);
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <>
            <div className="d-flex justify-content-center gap-2 mb-4">
                <Button 
                    variant="success" 
                    size="lg" 
                    className="shadow-sm"
                    onClick={() => setShowEmailModal(true)}
                >
                    <i className="fa fa-envelope me-2"></i>
                    {t('send_by_email')}
                </Button>
            </div>

            {/* Modal de Envio por Email */}
            <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-envelope text-success me-2"></i>
                        {t('send_coupon_by_email')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormGroup>
                        <FormLabel>{t('email')}</FormLabel>
                        <InputText
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('enter_email')}
                        />
                    </FormGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
                        {t('cancel')}
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleSendEmail}
                        disabled={sendingEmail || !email}
                    >
                        {sendingEmail ? (
                            <>
                                <LoadingIcon /> {t('sending')}...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-paper-plane me-2"></i>
                                {t('send')}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            <Card className="border-0 shadow-sm non-tax-coupon" ref={componentRef}>
                <Card.Body className="p-4" ref={ref}>
                    {
                        company ?
                            <>
                                {/* Cabeçalho da Empresa */}
                                <div className="text-center mb-4 pb-3 border-bottom border-2 border-primary">
                                    <h2 className="mb-2 text-primary fw-bold">{company.name}</h2>
                                    <h5 className="non-tax-coupon__muted mb-3">{company.corporateName}</h5>
                                    <p className="mb-1">
                                        <i className="fa fa-map-marker-alt text-primary me-2"></i>
                                        {company.address.street}, {company.address.number}
                                        {company.address.complement ? `, ${company.address.complement}` : ''}
                                    </p>
                                    <p className="mb-1">
                                        {company.address.neighborhood} - {formatZipCode(company.address.zipCode)}
                                    </p>
                                    <p className="mb-1">
                                        {company.address.city}/{company.address.state}
                                    </p>
                                    <p className="mb-0">
                                        <i className="fa fa-phone text-primary me-2"></i>
                                        {formatPhoneNumber(company.phone)}
                                    </p>
                                </div>

                                {/* Badge do Tipo de Cupom */}
                                <div className="text-center mb-4">
                                    <Badge bg="info" className="fs-5 px-4 py-2">
                                        <i className="fa fa-receipt me-2"></i>
                                        {t('non_tax_coupon')}
                                    </Badge>
                                </div>

                                {/* Informações do Pedido */}
                                <Card className="non-tax-coupon__panel mb-4 border-0">
                                    <Card.Body className="p-3">
                                        <Row>
                                            <Col md={6} className="mb-2">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-file-alt text-primary me-2 fs-5"></i>
                                                    <div>
                                                        <small className="non-tax-coupon__muted d-block">{t('cnpj')}</small>
                                                        <strong>{formatCNPJ(company.cnpj)}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-calendar text-primary me-2 fs-5"></i>
                                                    <div>
                                                        <small className="non-tax-coupon__muted d-block">{t('date')}</small>
                                                        <strong>{formatDateTime(order.requestDate)}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-hashtag text-primary me-2 fs-5"></i>
                                                    <div>
                                                        <small className="non-tax-coupon__muted d-block">{t('order_number')}</small>
                                                        <strong>#{order.number}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-user text-primary me-2 fs-5"></i>
                                                    <div>
                                                        <small className="non-tax-coupon__muted d-block">{t("customer")}</small>
                                                        <strong>{order.customer ? order.customer.fullName : '-'}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Tabela de Itens */}
                                <div className="mb-4">
                                    <h5 className="mb-3">
                                        <i className="fa fa-shopping-cart text-primary me-2"></i>
                                        {t('order_items')}
                                    </h5>
                                    <Table responsive className="align-middle non-tax-coupon__table">
                                        <thead>
                                            <tr>
                                                <th className="border-0">
                                                    <i className="fa fa-box me-2 text-primary"></i>
                                                    {t('item')}
                                                </th>
                                                <th className="border-0 text-center">
                                                    <i className="fa fa-calculator me-2 text-primary"></i>
                                                    {t('qty')} x {t('price')}
                                                </th>
                                                <th className="border-0 text-end">
                                                    <i className="fa fa-dollar-sign me-2 text-primary"></i>
                                                    {t('total')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Itens pelo valor antes do desconto da venda, para a soma
                                                das linhas bater com o subtotal e o desconto aparecer
                                                numa linha própria do resumo. */}
                                            {order.items.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td className="fw-semibold">{item.product.name}</td>
                                                    <td className="text-center">
                                                        <Badge className="non-tax-coupon__item-badge px-3 py-2">
                                                            {item.qty} x {t('currency_symbol')} {convertDoubleToStringPTBR(item.priceBeforeManualDiscount ?? item.price)}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end fw-bold text-primary">
                                                        {t('currency_symbol')} {convertDoubleToStringPTBR(item.totalBeforeManualDiscount ?? item.total)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Resumo Financeiro */}
                                <Card className="non-tax-coupon__panel border-0">
                                    <Card.Body className="p-3">
                                        <Row className="mb-2">
                                            <Col className="text-end">
                                                <span className="non-tax-coupon__muted">{t('subtotal')}:</span>
                                            </Col>
                                            <Col xs="auto">
                                                <strong>{t('currency_symbol')} {convertDoubleToStringPTBR(order.subTotal)}</strong>
                                            </Col>
                                        </Row>
                                        {order.manualDiscountValue > 0 && (
                                            <Row className="mb-2">
                                                <Col className="text-end">
                                                    <span className="non-tax-coupon__muted">{t('discount')}:</span>
                                                </Col>
                                                <Col xs="auto">
                                                    <strong>- {t('currency_symbol')} {convertDoubleToStringPTBR(order.manualDiscountValue)}</strong>
                                                </Col>
                                            </Row>
                                        )}
                                        <hr className="my-2" />
                                        <Row>
                                            <Col className="text-end">
                                                <h5 className="mb-0">{t('total')}:</h5>
                                            </Col>
                                            <Col xs="auto">
                                                <h4 className="mb-0 text-primary fw-bold">
                                                    {t('currency_symbol')} {convertDoubleToStringPTBR(order.total)}
                                                </h4>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Rodapé */}
                                <div className="text-center mt-4 pt-3 non-tax-coupon__border" style={{ borderTop: '1px solid' }}>
                                    <p className="non-tax-coupon__muted mb-0">
                                        <i className="fa fa-heart text-danger me-2"></i>
                                        {t('thank_you_message')}
                                    </p>
                                </div>
                            </>
                            :
                            <div className="text-center py-5">
                                <LoadingIcon />
                                <p className="non-tax-coupon__muted mt-3">{t('loading')}...</p>
                            </div>
                    }
                </Card.Body>
            </Card>

            <style jsx>{`
                @media print {
                    .btn {
                        display: none !important;
                    }
                }
            `}</style>
        </>
    );
})

export default NonTaxCouponGenerator;
